import type { StockShippingSchedule } from '../types'

/**
 * Shipping for a stock band order, from the schedule /bands/settings sends.
 *
 * A mirror of stockBandShippingCost() in api/v1/lib/stock-bands.php so the form
 * can show the member the shipping cost for the quantity typed before payment
 * starts. The server prices the charge itself from the same rows; this is
 * display only. stockBandShipping.test.ts pins the same quantities the PHP test
 * pins (100, 101, 599, 600, 1099, 1100, ..., 3000, 3001, 4000, 4001).
 *
 * Tiers are inclusive and ascending: the first tier whose upTo is at least the
 * quantity applies. Above the last tier the over-3000 formula applies: base plus
 * perThousand for every started thousand over 3000.
 *
 * There is one mailing option (Lauren Gandara, 2026-09-17: "We can just have one
 * mailing option and call it 'shipping' like you have it now").
 */
export function stockShippingCost(
  schedule: StockShippingSchedule,
  quantity: number,
): number {
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return 0
  }
  for (const tier of schedule.tiers) {
    if (quantity <= tier.upTo) {
      return round2(tier.shipping)
    }
  }
  const lastUpTo = schedule.tiers.length > 0 ? schedule.tiers[schedule.tiers.length - 1].upTo : 3000
  const over = quantity - lastUpTo
  return round2(schedule.over3000.base + Math.ceil(over / 1000) * schedule.over3000.perThousand)
}

/** Item cost plus shipping, the figure the form shows as the order total. */
export function stockOrderTotal(
  schedule: StockShippingSchedule,
  quantity: number,
): number {
  return round2(quantity * schedule.unitPrice + stockShippingCost(schedule, quantity))
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}
